import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { GET_MEDIA_BUYING_CONNECTED_DATA } from '../graphql/media-buying-queries'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { isEmpty } from '../utils/tools'
import { showNotification } from '../utils/notifications'

const MediaBuyingContext = createContext()

const connPublishersTransformer = (data) => {
    if (data && data.publisherUserAdAccounts) {
        return data.publisherUserAdAccounts?.reduce(
            (acc, connection) => {
                acc.connectedPublishers[connection.publisher_id] = connection
                if (connection.ad_account) {
                    acc.adAccounts[connection.publisher_id] = connection.ad_account
                }
                return acc
            },
            { adAccounts: {}, connectedPublishers: {} },
        )
    } else {
        return {
            adAccounts: {},
            connectedPublishers: {},
        }
    }
}

export const MediaBuyingProvider = ({ children }) => {
    const { id } = useParams()
    const { pathname } = useLocation()
    const navigate = useNavigate()

    const [section, subSection] = pathname.split('/').filter(Boolean).slice(-2) // if no account we only allow this route in media buying ["settings","ad-account"]

    const { data, loading, error, refetch } = useQuery(GET_MEDIA_BUYING_CONNECTED_DATA, {
        variables: { campaignId: id },
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            if (data && data.publisherUserAdAccounts) {
                const { adAccounts, connectedPublishers } = connPublishersTransformer(data)
                setValue({
                    hasPublishers: !isEmpty(connectedPublishers),
                    adAccounts: adAccounts || null,
                    connectedPublishers: connectedPublishers || null,
                    refetch: reloadData,
                    loading: false,
                    error: null,
                })

                if (section !== 'settings' && subSection !== 'ad-account') {
                    if (isEmpty(connectedPublishers)) {
                        showNotification(
                            `A connected Ad Account is needed for Media Buying. 
                        Please connect an Ad Account to continue.`,
                            'error',
                        )
                        navigate(`/campaigns/media-buying/${id}/settings/ad-account`, { replace: true })
                    }
                }
            }
        },
        onError: (error) => {
            setValue({
                hasPublishers: false,
                adAccounts: null,
                connectedPublishers: null,
                loading: false,
                error: new Error(error.message || 'Failed to fetch media buying data'),
            })
            console.error('MediaBuyingProvider error:', error)
        },
    })

    const reloadData = useCallback(() => {
        return refetch().then(({ data: fresh }) => {
            const { adAccounts, connectedPublishers } = connPublishersTransformer(fresh)
            setValue({
                hasPublishers: !isEmpty(connectedPublishers),
                adAccounts: adAccounts || null,
                connectedPublishers: connectedPublishers || null,
                refetch: reloadData,
                loading: false,
                error: null,
            })
            return fresh
        })
    }, [refetch])

    const [value, setValue] = useState({
        hasPublishers: false,
        adAccounts: null,
        connectedPublishers: null,
        refetch: reloadData,
        loading: true,
        error: null,
    })

    return <MediaBuyingContext.Provider value={value}>{children}</MediaBuyingContext.Provider>
}

/**
 * @typedef {Object} useMediaBuyingContext
 * @property {boolean} hasPublishers           — whether user has any connected publisher
 * @property {Object|null} adAccounts          — ad account, or null
 * @property {Object|null} connectedPublishers — connected publisher data (See Graphql query props), or null
 * @property {() => Promise<Object>} refetch   — function to update layout context data
 * @property {boolean} loading
 * @property {Error|null} error
 */
export const useMediaBuyingContext = () => {
    const ctx = useContext(MediaBuyingContext)
    if (!ctx) throw new Error('useMediaBuying must be used within MediaBuyingProvider')
    return ctx
}
